import React, { useState, useRef } from "react";
import "./App.css";

const STATUS_CONFIG = {
  DISCONNECTED: { bg: "#1e293b", label: "DISCONNECTED", icon: "○", textColor: "#94a3b8", pulse: false },
  CONNECTING:   { bg: "#b45309", label: "CONNECTING...", icon: "◌", textColor: "#ffffff", pulse: true  },
  CONNECTED:    { bg: "#1d4ed8", label: "CONNECTED",    icon: "●", textColor: "#ffffff", pulse: false },
  OPEN:         { bg: "#15803d", label: "OPEN",         icon: "✓", textColor: "#ffffff", pulse: false },
  CLOSED:       { bg: "#b91c1c", label: "CLOSED",       icon: "✕", textColor: "#ffffff", pulse: false },
  ERROR:        { bg: "#7f1d1d", label: "ERROR",        icon: "!", textColor: "#fca5a5", pulse: false },
};

function playBeep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(880, ctx.currentTime);
    gain.gain.setValueAtTime(0.6, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.6);
  } catch (e) {
    console.warn("Audio not available:", e);
  }
}

function App() {
  const [status, setStatus] = useState("DISCONNECTED");
  const [message, setMessage] = useState("");
  const [deviceInfo, setDeviceInfo] = useState(null);   // { name, id }
  const [messageLog, setMessageLog] = useState([]);     // last 10 BLE messages
  const lastStatusRef   = useRef("DISCONNECTED");
  const deviceRef       = useRef(null);   // saved BluetoothDevice
  const reconnectingRef = useRef(false); // guard against parallel reconnect loops

  // ESP32 UUIDs — commented out until hardware is available
  // const SERVICE_UUID = "4fafc201-1fb5-459e-8fcc-c5c9c331914b";
  // const CHAR_UUID    = "beb5483e-36e1-4688-b7f5-ea07361b26a8";

  const isConnected = ["CONNECTING", "CONNECTED", "OPEN", "CLOSED"].includes(status);

  // Attach notifications to a GATT server instance
  // (requires ESP32 with matching SERVICE_UUID / CHAR_UUID — disabled until hardware is ready)
  // const attachNotifications = async (server) => { ... };

  // Reconnect loop — retries every 3 s until successful
  const reconnect = async (device) => {
    if (reconnectingRef.current) return;
    reconnectingRef.current = true;
    setStatus("CONNECTING");
    setMessage("");

    while (reconnectingRef.current) {
      try {
        console.log("Attempting reconnect…");
        await device.gatt.connect();
        // await attachNotifications(server); // re-enable with ESP32
        playBeep();
        setStatus("CONNECTED");
        reconnectingRef.current = false;
        return;
      } catch (err) {
        console.warn("Reconnect failed, retrying in 3 s…", err);
        await new Promise((res) => setTimeout(res, 3000));
      }
    }
  };

  const disconnectBLE = () => {
    reconnectingRef.current = false; // stop any reconnect loop
    if (deviceRef.current?.gatt?.connected) {
      deviceRef.current.gatt.disconnect();
    }
    deviceRef.current = null;
    setStatus("DISCONNECTED");
    setMessage("");
    setDeviceInfo(null);
    setMessageLog([]);
    lastStatusRef.current = "DISCONNECTED";
  };

  const connectBLE = async () => {
    try {
      setStatus("CONNECTING");
      setMessage("");
      reconnectingRef.current = false;

      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: ["generic_access"], // needed to allow GATT connection to any device
        // optionalServices: [SERVICE_UUID], // re-enable with ESP32
      });

      deviceRef.current = device;
      setDeviceInfo({ name: device.name || "Unknown", id: device.id });

      // Listen for disconnection
      device.addEventListener("gattserverdisconnected", () => {
        console.log("Device disconnected — starting auto-reconnect");
        setStatus("CONNECTING");
        reconnect(device);
      });

      await device.gatt.connect();
      // await attachNotifications(server); // re-enable with ESP32
      playBeep();
      setStatus("CONNECTED");
    } catch (error) {
      console.error(error);
      setStatus("ERROR");
    }
  };

  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.DISCONNECTED;

  return (
    <div
      className="app-bg"
      style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/bg.jpg)` }}
    >
      <div className="app-overlay">
        <div className="app-content">

          {/* Logo & Brand */}
          <div className="brand-section">
            <img
              src={process.env.PUBLIC_URL + "/flinker-logo.avif"}
              alt="Flinker"
              className="brand-logo"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <span className="brand-name">FLINKER</span>
          </div>

          <p className="brand-subtitle">BLE Trunk Monitor</p>

          {/* Status Card */}
          <div
            className={`status-card${cfg.pulse ? " status-card--pulse" : ""}`}
            style={{ backgroundColor: cfg.bg }}
          >
            <span className="status-icon" style={{ color: cfg.textColor }}>
              {cfg.icon}
            </span>
            <span className="status-label" style={{ color: cfg.textColor }}>
              {cfg.label}
            </span>
            {cfg.pulse && <div className="status-progress-bar" />}
          </div>

          {/* Raw BLE Message */}
          {message && (
            <div className="message-box">
              <span className="message-header">Raw BLE Message</span>
              <code className="message-value">{message}</code>
            </div>
          )}

          {/* Connect / Disconnect Buttons */}
          {!isConnected && (
            <button
              className="connect-btn"
              onClick={connectBLE}
              disabled={status === "CONNECTING"}
            >
              {status === "CONNECTING" ? "Connecting…" : "Connect Device"}
            </button>
          )}

          {isConnected && status !== "CONNECTING" && (
            <button className="disconnect-btn" onClick={disconnectBLE}>
              Disconnect
            </button>
          )}

          {/* Debug Panel */}
          {deviceInfo && (
            <div className="debug-panel">
              <div className="debug-header">Debug Info</div>
              <div className="debug-row">
                <span className="debug-label">Device Name</span>
                <span className="debug-value">{deviceInfo.name}</span>
              </div>
              <div className="debug-row">
                <span className="debug-label">Device ID</span>
                <span className="debug-value debug-id">{deviceInfo.id}</span>
              </div>
              <div className="debug-row">
                <span className="debug-label">Connection</span>
                <span className="debug-value">{status}</span>
              </div>
              {messageLog.length > 0 && (
                <div className="debug-log-section">
                  <span className="debug-label">Message Log (last 10)</span>
                  <ul className="debug-log">
                    {messageLog.map((entry, i) => (
                      <li key={i} className="debug-log-entry">{entry}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

export default App;